import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { FilterOption } from '../types';

interface CardCheckboxGroupProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: FilterOption[];
  label?: string;
  description?: string;
  hasSelectAll?: boolean;
  disabled?: boolean;
  className?: string;
}

export const CardCheckboxGroup: React.FC<CardCheckboxGroupProps> = ({
  value,
  onChange,
  options,
  label,
  description,
  hasSelectAll = false,
  disabled = false,
  className,
}) => {
  const selectedValues = Array.isArray(value) ? value : [];

  const handleToggle = (optionValue: string) => {
    const newValue = selectedValues.includes(optionValue)
      ? selectedValues.filter((v) => v !== optionValue)
      : [...selectedValues, optionValue];
    onChange(newValue);
  };

  const handleSelectAll = () => {
    onChange(options.map((opt) => opt.value));
  };

  const handleSelectNone = () => {
    onChange([]);
  };

  const allSelected = selectedValues.length === options.length;
  const noneSelected = selectedValues.length === 0;

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {(label || description) && (
        <div className="flex flex-col gap-1">
          {label && (
            <label className="text-sm font-medium text-text-secondary">{label}</label>
          )}
          {description && (
            <p className="text-xs text-text-muted">{description}</p>
          )}
        </div>
      )}

      {hasSelectAll && (
        <div className="flex items-center gap-2 mb-1">
          <button
            type="button"
            onClick={handleSelectAll}
            disabled={disabled || allSelected}
            className={cn(
              'text-xs font-medium px-2 py-1 rounded inline-flex items-center',
              // 44 × 44 hitbox on touch; tighter on desktop. Negative margin
              // matches the FilterFieldShell pattern so visible padding stays
              // compact while the tappable area satisfies WCAG 2.5.5.
              'min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 -my-2 md:my-0',
              'text-accent-primary hover:bg-accent-primary/10',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'transition-colors'
            )}
          >
            Select All
          </button>
          <span className="text-text-muted" aria-hidden="true">|</span>
          <button
            type="button"
            onClick={handleSelectNone}
            disabled={disabled || noneSelected}
            className={cn(
              'text-xs font-medium px-2 py-1 rounded inline-flex items-center',
              'min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 -my-2 md:my-0',
              'text-text-muted hover:text-text-secondary hover:bg-surface-container',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'transition-colors'
            )}
          >
            Clear
          </button>
          <span className="ml-auto text-xs text-text-muted tabular-nums">
            {selectedValues.length}/{options.length}
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-2">
        {options.map((option) => {
          const isSelected = selectedValues.includes(option.value);

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => handleToggle(option.value)}
              disabled={disabled}
              role="checkbox"
              aria-checked={isSelected ? 'true' : 'false'}
              aria-label={option.label}
              tabIndex={0}
              className={cn(
                'relative flex items-start gap-2 px-2.5 py-2 rounded-lg border transition-all',
                // 44 px touch minimum on mobile so the whole card is tappable.
                'min-h-[44px] md:min-h-0',
                'focus:outline-none focus:ring-2 focus:ring-accent-primary',
                isSelected
                  ? 'bg-accent-primary/10 border-accent-primary text-text-primary ring-1 ring-accent-primary/40'
                  : 'bg-surface-container border-border-subtle text-text-secondary hover:border-accent-primary/50 hover:bg-surface-container-high',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
              onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleToggle(option.value);
                }
              }}
            >
              <div
                className={cn(
                  'flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors mt-0.5',
                  isSelected
                    ? 'bg-accent-primary border-accent-primary'
                    : 'border-border-subtle'
                )}
              >
                {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
              </div>

              <div className="flex flex-col items-start gap-0 min-w-0 flex-1">
                {option.badge && (
                  /* Mobile: badge stacks ABOVE the label so it never overlaps
                     "MA 5", "MA 100" etc on narrow 2-col cards (the previous
                     `absolute top-1 right-1` clipped the MA number).
                     Desktop (md+): float to top-right corner - there's room. */
                  <span
                    className="self-start mb-0.5 text-xs font-medium leading-none px-1 py-px rounded bg-amber-700 text-black md:absolute md:top-1 md:right-1 md:mb-0"
                    title={option.badge}
                  >
                    {option.badge}
                  </span>
                )}
                <span className="text-xs font-medium text-left truncate w-full leading-tight">
                  {option.label}
                </span>
                {option.subtitle && (
                  /* Allow 2 lines on mobile so users can read full descriptions
                     ("Large-cap U.S. stocks repr…" was the bug); single-line
                     truncate on desktop where horizontal space is plentiful. */
                  <span
                    className="text-xs text-text-muted w-full leading-tight line-clamp-2 md:line-clamp-none md:truncate"
                    title={option.subtitle}
                  >
                    {option.subtitle}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CardCheckboxGroup;
